import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Smartphone,
  Zap,
  Search,
  MessageSquare,
  Stethoscope,
  Home as HomeIcon,
  GraduationCap,
  Briefcase,
  Target,
  Users,
  Award,
  HeadphonesIcon
} from 'lucide-react';

export const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full mb-6 text-sm font-medium">
              <CheckCircle2 size={16} />
              Trusted by Businesses Across India
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Get More Customers With a
              <span className="text-blue-600"> Professional Website</span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-10 leading-relaxed">
              We help local businesses create websites that attract customers, build trust, and generate real inquiries.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 h-auto shadow-lg shadow-blue-600/20"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 h-auto border-2 border-slate-300 hover:border-blue-600 hover:text-blue-600"
              >
                See What We Do
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-600 pt-4 border-t border-slate-200 max-w-2xl mx-auto">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Based in Pune</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>15+ Projects Delivered</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>100% Client Satisfaction</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Does Your Business Have This Problem?
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              If you face any of these challenges, you are losing customers to competitors every day.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: 'No Proper Website',
                description: 'Running your business without a professional website in today\'s digital world.',
              },
              {
                title: 'Only Social Media',
                description: 'Relying only on Instagram or WhatsApp - missing out on Google search traffic.',
              },
              {
                title: 'Not Appearing on Google',
                description: 'Potential customers search for your services but cannot find you online.',
              },
              {
                title: 'Outdated Design',
                description: 'Old website design that makes your business look less professional than competitors.',
              },
              {
                title: 'No Inquiries Coming',
                description: 'Website exists but does not generate calls, messages, or leads for your business.',
              },
              {
                title: 'Competitors Look Better',
                description: 'Other businesses in your field have better online presence and are getting more clients.',
              },
            ].map((item, idx) => (
              <Card key={idx} className="p-6 bg-white border-slate-200 hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <Target className="text-red-600" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="services" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              What You Get With PageElevate
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              A complete website solution that makes it easy for customers to find you and contact you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Target size={28} />,
                title: 'Professional Design',
                description: 'Clean, attractive layout that makes your business look trustworthy and credible to customers.',
              },
              {
                icon: <Smartphone size={28} />,
                title: 'Works on All Devices',
                description: 'Your website will look great and work perfectly on mobile phones, tablets, and computers.',
              },
              {
                icon: <Zap size={28} />,
                title: 'Fast & Reliable',
                description: 'Quick loading pages that keep visitors on your site and help you rank better on Google.',
              },
              {
                icon: <MessageSquare size={28} />,
                title: 'Easy Contact Options',
                description: 'WhatsApp button, call button, and contact forms so customers can reach you instantly.',
              },
              {
                icon: <Search size={28} />,
                title: 'Google Search Ready',
                description: 'Built correctly so people can find your business when they search on Google.',
              },
              {
                icon: <CheckCircle2 size={28} />,
                title: 'Inquiry Forms That Work',
                description: 'Simple forms that collect customer details and send you notifications immediately.',
              },
            ].map((item, idx) => (
              <Card key={idx} className="p-8 bg-white border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Help Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Businesses We Help
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              We understand your industry and build websites that work specifically for your type of business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Stethoscope size={32} />,
                title: 'Doctors & Clinics',
                description: 'Build patient trust online. Make it easy for patients to book appointments and find your clinic.',
              },
              {
                icon: <HomeIcon size={32} />,
                title: 'Real Estate Agents',
                description: 'Show your properties professionally. Help serious buyers find and contact you easily.',
              },
              {
                icon: <GraduationCap size={32} />,
                title: 'Coaching Institutes',
                description: 'Get more student enrollments. Parents can see your courses and contact you directly.',
              },
              {
                icon: <Briefcase size={32} />,
                title: 'Service Businesses',
                description: 'Stand out from competitors. Show your expertise and make customers trust your services.',
              },
            ].map((item, idx) => (
              <Card key={idx} className="p-8 bg-white border-slate-200 hover:border-blue-300 transition-colors">
                <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4 text-white">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              How We Work
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Our straightforward process to build your professional website.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Understand Your Business',
                description: 'We talk to you about your business, your customers, and what you want your website to do.',
              },
              {
                step: '02',
                title: 'Create Your Website',
                description: 'We design and build your website with all the features you need to get customers.',
              },
              {
                step: '03',
                title: 'Set Up Everything',
                description: 'We make sure contact forms work, WhatsApp connects, and everything runs smoothly.',
              },
              {
                step: '04',
                title: 'Launch & Help You',
                description: 'We put your website live and teach you how to use it. We are available if you need help.',
              },
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="text-6xl font-bold text-blue-100 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
                
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-12 -right-4 w-8 h-0.5 bg-blue-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why PageElevate */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Why Businesses Trust Us
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              We focus on delivering real results, not just a website. Here's what makes us different.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: <Users size={24} />,
                title: 'Clear, Honest Communication',
                description: 'We explain everything in simple language. No confusing technical terms. You always know what is happening.',
              },
              {
                icon: <Target size={24} />,
                title: 'We Focus on Getting You Customers',
                description: 'Your website is built to get inquiries and customers, not just to look good. Results matter to us.',
              },
              {
                icon: <Award size={24} />,
                title: 'Quality Work, On Time',
                description: 'We deliver professional websites on schedule. No delays, no excuses. We keep our promises.',
              },
              {
                icon: <CheckCircle2 size={24} />,
                title: 'We Understand Indian Businesses',
                description: 'We know what works for Indian customers and businesses. Our websites are built for the local market.',
              },
              {
                icon: <HeadphonesIcon size={24} />,
                title: 'Support When You Need It',
                description: 'Have a question? Need help? We respond quickly. You can reach us on call or WhatsApp anytime.',
              },
              {
                icon: <Zap size={24} />,
                title: 'Limited Projects = Better Service',
                description: 'We take only a few projects each month. This means we can focus on your success properly.',
              },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Ready to Get More Customers Online?
          </h2>
          
          <p className="text-xl text-slate-300 mb-10 leading-relaxed">
            Book a free call with us. We will understand your business and explain exactly how a website can help you grow.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-10 py-6 h-auto shadow-lg shadow-blue-600/20"
            >
              Schedule Your Free Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              onClick={() => window.open('https://wa.me/917620124046?text=Hi%2C%20I%20want%20to%20discuss%20website%20development', '_blank')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-10 py-6 h-auto"
            >
              <MessageSquare className="mr-2" size={20} />
              WhatsApp Us
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-500" />
              <span>30-minute free call</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-500" />
              <span>No obligation</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-500" />
              <span>Clear pricing</span>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800">
            <p className="text-slate-400 mb-4">Or reach us directly:</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-slate-300">
              <a href="mailto:hello@pageelevate.com" className="hover:text-white transition-colors">
                📧 hello@pageelevate.com
              </a>
              <a href="tel:+917620124046" className="hover:text-white transition-colors">
                📞 +91 7620124046
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
